import { useEffect, useState } from 'react';

export type ScrollDirection = 'up' | 'down';

/**
 * useScrollDirection
 * Detects if the user is scrolling up or down.
 *
 * @param threshold Optional minimum pixel delta before updating the direction. Default is 50.
 * @returns The current scroll direction: 'up' or 'down'.
 */
export function useScrollDirection(threshold: number = 50): ScrollDirection {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('up');

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const updateScrollDirection = () => {
      const { scrollY } = window;
      // Only update if the scroll is beyond the threshold
      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false;
        return;
      }

      const newDirection: ScrollDirection = scrollY > lastScrollY ? 'down' : 'up';
      setScrollDirection(newDirection); // Update immediately
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;

      // Clear any pending debounce to avoid delayed updates
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking = true;
      }

      // Reset timeout to detect scroll stop
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // If scrolling stops and we're near the top, ensure 'up' is set
        if (window.scrollY < threshold && scrollDirection !== 'up') {
          setScrollDirection('up');
        }
      }, 150); // Slight delay to confirm scroll has stopped
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [threshold, scrollDirection]);

  return scrollDirection;
}
